import { message } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import config from "../../../config";
import { getAsset } from "../../../services/general";
import { allowedFileCriteria, getBase64 } from "../../../utils/fileUtils";
import { useTranslation } from "react-i18next";

const useDNIPhoto = ({
  form,
  patientToAdmission,
  setLoading,
  countryCode,
  field,
}) => {
  const [method, setMethod] = useState();
  const [visible, setVisible] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [imageType, setImageType] = useState(null);
  const [dniFile, setDniFile] = useState();
  const webcamRef = React.useRef(null);
  const { t } = useTranslation();

  const videoConstraints = {
    width: 741,
    height: 720,
  };

  const setPhoto = useCallback(
    (photo, isNew = true, type = 'image/png') => {
      setImageSrc(photo);
      setImageType(type);
      if (form) {
        form.setFieldsValue({ [field]: { photo, type, isNew } });
      }
    },
    [field, form]
  );

  useEffect(() => {
    if (countryCode === "PE") {
      setMethod("webcam");
    }
  }, [countryCode]);

  const capture = useCallback(() => {
    if (imageSrc && !imageSrc.includes("missing")) {
      setVisible(false);
      return;
    }
    const imageFromCapture = webcamRef.current.getScreenshot();
    setPhoto(imageFromCapture);
  }, [imageSrc, setPhoto]);

  const resetImage = useCallback(() => {
    setPhoto(null);
    setDniFile(undefined);
  }, [setPhoto]);

  const openWebcam = useCallback(() => {
    setVisible(true);
    if (webcamRef.current) {
      const stream = webcamRef.current.stream;
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach((track) => {
          track.stop();
        });
      }
    }
  }, [webcamRef]);

  const fetchPhoto = useCallback(
    async (params) => {
      try {
        setLoading((loading) => {
          return { ...loading, idCard: true };
        });
        const response = await getAsset({
          id: params.id,
          type: "Patient",
          field,
        });
        setPhoto(response.document_url, false, response.document_type);
      } catch (error) {
        console.error(error);
        message.error({
          content: "No se pudo cargar la foto del paciente",
          style: {
            marginTop: "35vh",
            fontSize: 16,
          },
        });
        setPhoto(null);
      } finally {
        setLoading((loading) => {
          return { ...loading, idCard: false };
        });
      }
    },
    [field, setLoading, setPhoto]
  );

  const openCameraToc = useCallback(() => {
    let url = config.validatorUrl;
    let params = "/TOC/documentPhoto";
    let target = "_blank";
    let options = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=0,height=0,left=-1000,top=-1000`;

    window.addEventListener(
      "message",
      (event) => {
        if (event.origin.startsWith(url)) {
          setPhoto(event.data);
          setMethod(null);
        }
      },
      false
    );

    window.open(`${url}${params}`, target, options);
  }, [setPhoto]);

  const selectMethod = (selectedMethod) => {
    setMethod(selectedMethod);
    if (selectedMethod === "toc") {
      openCameraToc();
    } else {
      openWebcam();
    }
  };

  const openModal = useCallback(() => {
    setVisible(true);

    if (countryCode === "PE") {
      openWebcam();
    }
  }, [countryCode, openWebcam]);

  const closeModal = useCallback(() => {
    setVisible(false);
    setMethod(countryCode === "PE" ? "webcam" : null);
    if (webcamRef.current) {
      const stream = webcamRef.current.stream;
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach((track) => {
          track.stop();
        });
      }
    }
  }, [countryCode]);

  const beforeUpload = useCallback(async (file) => {
    const isAllowed = allowedFileCriteria(t, file, 2);
    if (isAllowed) {
      setDniFile(file)
      const base64 = await getBase64(file)
      setPhoto(base64, true, file.type)
    }
    return isAllowed;
  }, [setPhoto, t])

  return {
    visible,
    openModal,
    closeModal,
    capture,
    imageSrc,
    webcamRef,
    resetImage,
    videoConstraints,
    fetchPhoto,
    method,
    selectMethod,
    setPhoto,
    beforeUpload,
    dniFile,
    imageType,
  };
};

export default useDNIPhoto;
